import { useNavigate, useSearch } from '@tanstack/react-router';
import type { LngLatBoundsLike, MapProps, ViewStateChangeEvent } from '@vis.gl/react-maplibre';
import { Map } from '@vis.gl/react-maplibre';
// eslint-disable-next-line import-x/no-unresolved
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { Fragment, lazy, Suspense, useCallback, useEffect } from 'react';

import basemapOptimizedStyle from '@/assets/map-styles/basemap-optimized.json?url';
import satelliteStyle from '@/assets/map-styles/satellite.json?url';
import { CustomAttributionControl } from '@/components/map/CustomAttributionControl';
import { MapSprite } from '@/components/map/MapSprite';
import { StateBorderLayer } from '@/components/map/StateBorderLayer';
import { useDeepestMatch } from '@/hooks/useDeepestMatch';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { useMapStore } from '@/stores/useMapStore';
import { MapLayer } from '@/types/MapLayer';
import { MapStyle } from '@/types/MapStyle';

const AdminLevelsActiveLayer = lazy(async () => ({
  default: (await import('@/components/map/AdminLevelsActiveLayer')).AdminLevelsActiveLayer,
}));
const BicycleNetworkLayer = lazy(async () => ({
  default: (await import('@/components/map/BicycleNetworkLayer')).BicycleNetworkLayer,
}));
const RouteSignsLayer = lazy(async () => ({
  default: (await import('@/components/map/RouteSignsLayer')).RouteSignsLayer,
}));
const ServiceStationsLayer = lazy(async () => ({
  default: (await import('@/components/map/ServiceStationsLayer')).ServiceStationsLayer,
}));

const LAYERS = {
  [MapLayer.BICYCLE_NETWORK]: BicycleNetworkLayer,
  [MapLayer.ROUTE_SIGNAGE]: RouteSignsLayer,
  [MapLayer.SERVICE_STATION]: ServiceStationsLayer,
  [MapLayer.ADMIN_LEVELS_ACTIVE]: AdminLevelsActiveLayer,
};

type ReportMapProps = Omit<MapProps, 'mapStyle'>;

export function BaseMap({ children, ...props }: ReportMapProps) {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  let initialViewState: MapProps['initialViewState'] | undefined;
  if (search.grenze?.length === 4) {
    initialViewState = { bounds: search.grenze as LngLatBoundsLike, fitBoundsOptions: { padding: 16 } };
  } else {
    const longitude = search.laenge;
    const latitude = search.breite;

    if (longitude && latitude) {
      initialViewState = {
        longitude,
        latitude,
        zoom: search.zoom ?? MAP_FLY_TO_ZOOM,
      };
    }
  }

  useEffect(() => {
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, grenze: undefined }),
    });
  }, [navigate]);

  const style = useMapStore((state) => state.style);
  const layers = useMapStore((state) => state.layers);

  const match = useDeepestMatch();

  const isPublicRoot = match.fullPath === '/';

  const handleMoveEnd = useCallback(
    async (event: ViewStateChangeEvent) => {
      if (isPublicRoot) {
        await navigate({
          to: '.',
          search: (prev) => ({
            ...prev,
            laenge: parseFloat(event.viewState.longitude.toFixed(6)),
            breite: parseFloat(event.viewState.latitude.toFixed(6)),
            zoom: parseFloat(event.viewState.zoom.toFixed(1)),
          }),
        });
      }
    },
    [isPublicRoot, navigate],
  );

  return (
    <Map
      {...props}
      attributionControl={false}
      bearing={0}
      dragRotate={false}
      minZoom={5}
      onMoveEnd={handleMoveEnd}
      pitch={0}
      touchPitch={false}
      workerUrl={workerUrl}
      initialViewState={
        initialViewState ?? {
          bounds: [7.49431099600789, 47.4685671818621, 10.66599079533, 49.8045457845908],
          fitBoundsOptions: { padding: 16 },
        }
      }
      mapStyle={
        {
          [MapStyle.VECTOR]: basemapOptimizedStyle,
          [MapStyle.SATELLITE]: satelliteStyle,
        }[style]
      }
    >
      <MapSprite />
      <CustomAttributionControl key={style} />

      <StateBorderLayer />

      {children}

      <Fragment>
        {Object.values(MapLayer).map((layer) => {
          if (!layers[layer]) {
            return null;
          }

          const Component = LAYERS[layer];

          return (
            <Suspense key={layer}>
              <Component />
            </Suspense>
          );
        })}
      </Fragment>
    </Map>
  );
}
